import { Box, CircularProgress, Container, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { parsedAny } from "@thalamos/common";
import type {
  AvailableOrganisationArray,
  ExtendedVisionUser,
  FormBuilderField,
  UserOrganisationRole,
} from "@thalamos/vision-common";
import {
  AvailableOrganisationArraySchema,
  emailSchema,
  FieldBuilder,
  isCurrentUserSupervisor,
  nameSchema,
  OrganisationRoleSchema,
  orgIdSchema,
  tk,
  UserOrganisationRoleSchema,
} from "@thalamos/vision-common";

import api, { apiHooks } from "../../api/index.js";
import { OrganisationContext } from "../../components/FormBuilder/OrganisationDropdown/OrganisationContext.js";
import { SinglePageForm } from "../../components/FormBuilder/SinglePageForm/SinglePageForm.js";
import { TypographyI18N } from "../../components/index.js";
import type { UserContextType } from "../../Context/UserContext.js";
import { useUser } from "../../Context/UserContext.js";
import { useToastNotifications } from "../../hooks/useToastNotifications.js";
import { Error as ErrorComponent } from "../../pages/Error/Error.js";

const UserProfileFormDataSchema = z.object({
  name: new FieldBuilder().withSchema(nameSchema).build().schema,
  email: new FieldBuilder().withSchema(emailSchema).build().schema,
  orgId: new FieldBuilder().withSchema(orgIdSchema).captureValueLabel().build()
    .schema,
  shoulderNumber: new FieldBuilder().withSchema(z.string()).build().schema,
  role: new FieldBuilder().withSchema(UserOrganisationRoleSchema).build()
    .schema,
});

type UserProfileFormData = z.infer<typeof UserProfileFormDataSchema>;

const UserProfileForm = ({
  onSubmit,
  onCancel,
  user,
}: {
  onSubmit: (values: UserProfileFormData) => void;
  onCancel: () => void;
  user: ExtendedVisionUser;
}) => {
  const { t } = useTranslation();
  const isSupervisor = isCurrentUserSupervisor(user);

  const displayRole = (role: UserOrganisationRole) => {
    switch (role) {
      case UserOrganisationRoleSchema.enum.Officer:
        return t("role.officer");
      case UserOrganisationRoleSchema.enum.Supervisor:
        return t("role.supervisor");
      case UserOrganisationRoleSchema.enum.ClientAdmin:
        return t("role.clientAdmin");
      case UserOrganisationRoleSchema.enum.AnalyticsViewer:
        return t("role.analyticsViewer");
      case UserOrganisationRoleSchema.enum.GuestUser:
        return t("role.guestUser");
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const content: { fields: FormBuilderField<UserProfileFormData, any>[] } = {
    fields: [
      {
        type: "typography",
        label: tk("userProfile.email"),
        textFn: ({ formValues }) => [formValues.email.value],
        fieldName: "email",
      },
      {
        type: "textbox",
        fieldName: "name",
        label: tk("userProfile.name"),
      },
      {
        type: "textbox",
        fieldName: "shoulderNumber",
        label: tk("userProfile.shoulderNumber"),
      },
      ...(!isSupervisor
        ? [
            {
              type: "organisation" as const,
              fieldName: "orgId",
              label: tk("userProfile.organisationMembership"),
              // Include orgs marked as excludeAsIncidentLocation so that
              // non-BCU staff can select an appropriate home base on their
              // profile, just as they can during onboarding.
              includeExcludedAsIncidentLocation: true,
            },
          ]
        : []),
      {
        type: "typography",
        label: tk("userProfile.role"),
        textFn: ({ formValues }) => [displayRole(formValues.role.value)],
        fieldName: "role",
      },
    ],
  };

  return (
    <SinglePageForm<typeof UserProfileFormDataSchema>
      schema={UserProfileFormDataSchema}
      onSubmit={onSubmit}
      content={content}
      initialValues={{
        name: { value: user.name },
        email: { value: user.email },
        shoulderNumber: { value: user.shoulderNumber ?? "" },
        orgId: {
          value: user.sessionContext?.organisationId ?? "",
          valueLabel: user.sessionContext?.organisationName ?? "",
        },
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion, @typescript-eslint/no-non-null-asserted-optional-chain
        role: { value: user.sessionContext?.role! },
      }}
      canCancel={true}
      onCancel={onCancel}
      confirmButtonLabel={tk("userProfile.confirm")}
      ariaLabel="User Profile"
    />
  );
};

export type UserProfileProps = {
  user: ExtendedVisionUser;
  onCancel: () => void;
  refetchUser: UserContextType["refetchUser"];
  onSubmit?: (data: {
    name: string;
    organisationId: string;
    shoulderNumber: string;
  }) => void;
};

export const UserProfile = ({
  user,
  onCancel,
  refetchUser,
  onSubmit: onSubmitOverride,
}: UserProfileProps) => {
  const theme = useTheme();
  const notifications = useToastNotifications();
  const { t } = useTranslation();

  return (
    <Box>
      <TypographyI18N
        translationKey={tk("userProfile.title")}
        sx={{
          paddingBottom: theme.spacing(2),
          fontWeight: 700,
        }}
      />
      <TypographyI18N
        translationKey={tk("userProfile.banner")}
        sx={{
          paddingBottom: theme.spacing(4),
        }}
      />
      <UserProfileForm
        user={user}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmit={async (values) => {
          const data = {
            name: values.name.value,
            organisationId: values.orgId.value,
            shoulderNumber: values.shoulderNumber.value,
          };

          if (onSubmitOverride) {
            onSubmitOverride(data);
            return;
          }

          try {
            const response = await api.user.update(user.id, data);
            if (response.status === 204) {
              notifications.show(t("userProfile.success"), {
                severity: "success",
              });

              await refetchUser();
            } else {
              throw new Error(JSON.stringify(response.data.message));
            }
          } catch {
            notifications.show(t("userProfile.error"), {
              severity: "error",
            });
          }
        }}
        onCancel={onCancel}
      />
    </Box>
  );
};

export const UserProfilePage = () => {
  const {
    user,
    loading: userLoading,
    error: userError,
    refetchUser,
  } = useUser();
  const navigate = useNavigate();
  const theme = useTheme();

  if (userLoading || user === undefined || user.sessionContext === null) {
    return <CircularProgress />;
  }

  if (userError) {
    return <ErrorComponent />;
  }

  const [{ data: organisationsResponse }] =
    apiHooks.organisation.getAvailableOrganisations(
      user.sessionContext.organisationId,
    );

  const availableOrganisations = parsedAny(
    AvailableOrganisationArraySchema,
    organisationsResponse,
  ).unwrapOr([]) as AvailableOrganisationArray;

  // Requested as part of this ticket (ASP-2664) just filter by PoliceCommandUnit for now
  const commandUnitsOnly = availableOrganisations.filter(
    (org) =>
      org.role === OrganisationRoleSchema.enum.PoliceCommandUnit ||
      org.role === OrganisationRoleSchema.enum.PoliceForce,
  );

  return (
    <OrganisationContext.Provider value={{ organisations: commandUnitsOnly }}>
      <Container sx={{ marginTop: theme.spacing(2) }}>
        <UserProfile
          user={user}
          onCancel={() => {
            navigate(-1);
          }}
          refetchUser={refetchUser}
        />
      </Container>
    </OrganisationContext.Provider>
  );
};
